/**
 * NotificationBell.jsx - the bell in the top bar (Sharif, 10/11/26): "a
 * notification bell ... that also tells what updates were made for each user
 * depending on what they are allowed to access and see" - the updates being
 * "done to the website itself from me when I deploy it".
 *
 * The badge counts the unread For you items (a small dot when only What's new
 * has something unseen). A click opens a popover (not a modal - the page stays
 * usable) with two tabs:
 *   For you     tasks assigned to me, tags and @mentions, views shared with me,
 *               PO's / Won on my records, my overdue tasks - each read / unread
 *   What's new  GlobalOps' release notes (server/data/releaseNotes.json,
 *               published on deploy), only the items my access can see
 *               (server/lib/releases.js); opening the tab marks them seen
 * A For you item opens its record the way the Activity log does
 * (notificationModel.js destinationOf) and is marked read; a What's new item
 * with a tab opens that tab. Filter chips (For you), "Load more", "Mark all
 * read", and Notification settings (per kind on / off, and the task emails).
 * Live: a new item arrives through the live stream (kind 'notifications', sent
 * only to its recipient) and bumps the badge at once; after a deploy the
 * stream reconnects and resyncs, which re-counts What's new.
 *
 * Approval (Sharif, 10/12/26): a deployed release waits, unseen by others,
 * until an approver (the bootstrap admin, "Can manage access") decides. His
 * What's new opens with "Waiting for your approval" - each release as people
 * will see it, with Notify everyone (new in their bell, the badge pops live),
 * Publish quietly (listed, no badge) and Hide. A quiet one can still notify
 * everyone, a published one can be hidden, and a hidden one (under "Hidden
 * from others") published after all. The pending ones count on his badge.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../ui/Icon';
import { useApp } from '../../context/AppContext';
import { useMyAccess } from '../../hooks/useMyAccess';
import { canSeeTab } from '../../data/roles';
import { useLiveEvent, useLiveRefresh } from '../../live/useLive';
import {
  NOTIFY_URL,
  KIND_META,
  FOR_YOU_CHIPS,
  timeAgo,
  badgeText,
  destinationOf,
  releaseDay,
  releaseDestination,
} from './notificationModel';

const GREEN = '#1B5E20';
const PAGE = 30;

/** One fetch: { ok, status, json }. Never throws. */
async function call(url, init) {
  try {
    const r = await fetch(url, init);
    const json = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, json };
  } catch {
    return { ok: false, status: 0, json: {} };
  }
}
const post = (url, body) =>
  call(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

const byId = (list) => (Array.isArray(list) && list.length ? new Map(list.map((x) => [String(x.id), x])) : null);
const EMPTY_LIST = { items: [], next: null, loading: false, error: '', loaded: false };
const TABS = [
  ['foryou', 'For you', 'forYou'],
  ['whatsnew', "What's new", 'whatsNew'],
];
// an approver's choices on a release, by its status now
const DECISIONS = {
  pending: [
    ['notified', 'Notify everyone'],
    ['quiet', 'Publish quietly'],
    ['hidden', 'Hide'],
  ],
  quiet: [
    ['notified', 'Notify everyone'],
    ['hidden', 'Hide'],
  ],
  notified: [['hidden', 'Hide']],
  hidden: [
    ['notified', 'Notify everyone'],
    ['quiet', 'Publish quietly'],
  ],
};

function Chip({ on, children, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={[
        'px-2.5 py-1 rounded-full text-[10.5px] font-bold border whitespace-nowrap',
        on ? 'bg-gat-greenDarkest text-white border-gat-greenDarkest' : 'bg-white text-text-body border-border hover:border-gat-green',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Toggle({ label, on, onChange }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 text-[12px] text-text-dark cursor-pointer">
      <span>{label}</span>
      <input type="checkbox" checked={!!on} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#1B5E20]" />
    </label>
  );
}

export default function NotificationBell() {
  const { allProjects, allAccounts, allEngineers } = useApp();
  const access = useMyAccess();

  const [counts, setCounts] = useState({ forYou: 0, whatsNew: 0 });
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('foryou');
  const [chip, setChip] = useState('all'); // For you's filter
  const [lists, setLists] = useState({ foryou: EMPTY_LIST, whatsnew: EMPTY_LIST });
  const [notes, setNotes] = useState({}); // item key -> why it did not open
  const [settings, setSettings] = useState(null); // the settings view, when shown
  const [coords, setCoords] = useState({ top: 64, right: 16 });
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const seq = useRef({ foryou: 0, whatsnew: 0 }); // only the newest read of a tab lands
  const [deciding, setDeciding] = useState({}); // release id -> the status being saved / an error

  const takeCounts = useCallback((c) => {
    if (c && typeof c.forYou === 'number') setCounts({ forYou: c.forYou, whatsNew: Number(c.whatsNew) || 0 });
  }, []);
  const refreshCounts = useCallback(async () => {
    const r = await call(`${NOTIFY_URL}/counts`);
    if (r.ok) takeCounts(r.json);
  }, [takeCounts]);
  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const urlOf = useCallback(
    (which, before) => {
      if (which === 'whatsnew') return `${NOTIFY_URL}/releases`;
      const u = new URLSearchParams({ limit: String(PAGE) });
      if (before) u.set('before', String(before));
      const c = FOR_YOU_CHIPS.find((x) => x.key === chip);
      if (c && c.kinds) u.set('kinds', c.kinds.join(','));
      return `${NOTIFY_URL}?${u}`;
    },
    [chip]
  );

  /** Page 1 of a tab (or the next page of For you with `more`). What's new: then marked seen. */
  const load = useCallback(
    async (which, { more = false, quietly = false } = {}) => {
      const mine = ++seq.current[which];
      const before = more ? lists[which].next : null;
      if (!quietly || more) setLists((l) => ({ ...l, [which]: { ...l[which], loading: true, error: '' } }));
      const r = await call(urlOf(which, before));
      if (mine !== seq.current[which]) return;
      if (!r.ok) {
        setLists((l) => ({ ...l, [which]: { ...l[which], loading: false, error: 'Could not load your notifications - try again.' } }));
        return;
      }
      const got = which === 'whatsnew' ? r.json.releases : r.json.items;
      const items = Array.isArray(got) ? got : [];
      setLists((l) => ({
        ...l,
        [which]: {
          items: more ? [...l[which].items, ...items] : items,
          next: r.json.nextBefore || null,
          loading: false,
          error: '',
          loaded: true,
          canApprove: which === 'whatsnew' && r.json.canApprove === true,
        },
      }));
      takeCounts(r.json.counts);
      // the "New" marks stay while the tab is open; the server forgets them now
      if (which === 'whatsnew' && r.json.counts && r.json.counts.whatsNew > 0) {
        const s = await post(`${NOTIFY_URL}/releases/seen`);
        if (s.ok) takeCounts(s.json);
      }
    },
    [lists, urlOf, takeCounts]
  );

  // opening, switching tab or chip reads that tab afresh
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });
  useEffect(() => {
    if (open && !settings) loadRef.current(tab);
  }, [open, tab, chip, settings]);

  // ── live ──
  // a new item of my own, or a release an approver just published: the badge
  // at once, and the open list re-reads
  useLiveEvent(
    (e) => !!e && e.kind === 'notifications',
    (e) => {
      refreshCounts();
      const releases = Array.isArray(e.fields) && e.fields.includes('releases');
      if (open && !settings && (tab === 'foryou' ? !releases : releases)) loadRef.current(tab, { quietly: true });
    }
  );
  // a deploy restarts the API: the stream comes back with a resync, and new
  // release notes may be waiting (no event of this kind is ever sent otherwise)
  useLiveRefresh('releases', refreshCounts);

  // ── the popover ──
  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setCoords({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    }
    setSettings(null);
    setNotes({});
    setOpen((v) => !v);
  };
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // what a click is checked against: the records the app holds, the tabs I may open
  const ctx = useMemo(() => {
    const contacts = new Map();
    const entries = new Map();
    for (const a of allAccounts || []) {
      for (const c of a.contacts || []) contacts.set(String(c.id), c);
      for (const e of a.recent_activity || []) entries.set(String(e.id), String(e.account_id || e.site_id || a.id));
    }
    const known = Array.isArray(allAccounts) && allAccounts.length > 0;
    return {
      projects: byId(allProjects),
      accounts: byId(allAccounts),
      contacts: known ? contacts : null,
      entries: known ? entries : null,
      engineers: byId(allEngineers),
      canSee: (t) => canSeeTab(access, t),
    };
  }, [allProjects, allAccounts, allEngineers, access]);
  const go = async (key, item) => {
    const to = destinationOf(item, ctx);
    if (to.text) {
      setNotes((n) => ({ ...n, [key]: to.text }));
      return;
    }
    if (to.verify) {
      setNotes((n) => ({ ...n, [key]: 'Opening…' }));
      const r = await call(to.verify.url);
      if (r.status === 404 || (r.ok && !to.verify.pick(r.json))) {
        setNotes((n) => ({ ...n, [key]: to.verify.missing }));
        return;
      }
      setNotes((n) => ({ ...n, [key]: '' }));
    }
    setOpen(false);
    window.location.hash = to.href;
  };

  const openForYou = (item) => {
    if (!item.read) {
      setLists((l) => ({ ...l, foryou: { ...l.foryou, items: l.foryou.items.map((x) => (x.id === item.id ? { ...x, read: true } : x)) } }));
      setCounts((c) => ({ ...c, forYou: Math.max(0, c.forYou - 1) }));
      post(`${NOTIFY_URL}/${item.id}/read`).then((r) => {
        if (r.ok) takeCounts(r.json);
      });
    }
    go(`f${item.id}`, item);
  };

  const openRelease = (key, item) => {
    const to = releaseDestination(item, ctx.canSee);
    if (!to) return;
    if (to.text) {
      setNotes((n) => ({ ...n, [key]: to.text }));
      return;
    }
    setOpen(false);
    window.location.hash = to.href;
  };

  // an approver's decision on a release; the reply is his What's new afresh
  const decide = async (id, status) => {
    setDeciding((d) => ({ ...d, [id]: { busy: status } }));
    const r = await post(`${NOTIFY_URL}/releases/${encodeURIComponent(id)}/status`, { status });
    if (!r.ok) {
      setDeciding((d) => ({ ...d, [id]: { error: r.json.error || 'Could not save that - try again.' } }));
      return;
    }
    setDeciding((d) => {
      const n = { ...d };
      delete n[id];
      return n;
    });
    if (Array.isArray(r.json.releases)) {
      setLists((l) => ({ ...l, whatsnew: { ...l.whatsnew, items: r.json.releases, canApprove: r.json.canApprove === true } }));
    }
    takeCounts(r.json.counts);
  };

  const markAll = async () => {
    setLists((l) => ({ ...l, foryou: { ...l.foryou, items: l.foryou.items.map((x) => ({ ...x, read: true })) } }));
    setCounts({ forYou: 0, whatsNew: 0 });
    const r = await post(`${NOTIFY_URL}/read-all`, { tab: 'all' });
    if (r.ok) takeCounts(r.json);
  };

  const showSettings = async () => {
    setSettings({ loading: true });
    const r = await call(`${NOTIFY_URL}/settings`);
    setSettings(r.ok ? r.json : { error: 'Could not load your settings - try again.' });
  };
  const setPref = async (group, key, on) => {
    setSettings((s) => ({ ...s, [group]: { ...s[group], [key]: on } }));
    const r = await call(`${NOTIFY_URL}/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [group]: { [key]: on } }),
    });
    if (r.ok) setSettings(r.json);
    else setSettings((s) => ({ ...s, [group]: { ...s[group], [key]: !on }, error: 'Could not save that - try again.' }));
  };

  const list = lists[tab];
  const releaseCard = (r) => {
    const choices = list.canApprove ? DECISIONS[r.status] || [] : [];
    const d = deciding[r.id] || {};
    const pending = r.status === 'pending';
    return (
      <li
        key={r.id}
        data-testid={`release-${r.id}`}
        className={[
          'go-fade-in px-4 py-3 border-b border-border last:border-b-0',
          pending ? 'bg-[#FFFBF0]' : r.isNew ? 'bg-[#F3F8F1]' : 'bg-white',
        ].join(' ')}
      >
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] font-semibold text-text-muted">{releaseDay(r.date)}</span>
          {r.isNew ? (
            <span className="px-1.5 rounded-full bg-gat-green text-white text-[9.5px] font-bold uppercase tracking-[0.6px]">New</span>
          ) : null}
          {list.canApprove && r.status === 'quiet' ? (
            <span
              title="Published without a notification"
              className="px-1.5 rounded-full border border-border text-text-muted text-[9.5px] font-bold uppercase tracking-[0.6px]"
            >
              Quiet
            </span>
          ) : null}
        </div>
        <div
          className={[
            'mt-0.5 text-[12.5px] leading-snug',
            r.isNew || pending ? 'font-bold text-text-dark' : 'font-semibold text-text-dark',
          ].join(' ')}
        >
          {r.title}
        </div>
        <ul className="mt-1.5 mb-0 pl-0 list-none space-y-1">
          {(r.items || []).map((it, i) => {
            const key = `r${r.id}:${i}`;
            return (
              <li key={key} className="flex gap-2 text-[11.5px] leading-snug text-text-body">
                <span aria-hidden="true" className="shrink-0 mt-[6px] w-1 h-1 rounded-full bg-gat-green" />
                <span className="flex-1 min-w-0">
                  {it.tab ? (
                    <button
                      type="button"
                      onClick={() => openRelease(key, it)}
                      className="text-left hover:text-gat-greenDarkest hover:underline focus:outline-none focus:underline"
                    >
                      {it.text}
                    </button>
                  ) : (
                    it.text
                  )}
                  {notes[key] ? (
                    <span role="status" className="block text-[11px] text-[#B71C1C] mt-0.5">
                      {notes[key]}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
        {choices.length ? (
          <div role="group" aria-label={`Publish ${r.title}`} className={['flex flex-wrap gap-1.5', pending ? 'mt-2.5' : 'mt-2'].join(' ')}>
            {choices.map(([status, text]) => (
              <button
                key={status}
                type="button"
                onClick={() => decide(r.id, status)}
                disabled={!!d.busy}
                className={[
                  'rounded-md font-bold disabled:opacity-50',
                  pending ? 'px-2.5 py-1 text-[11px]' : 'px-2 py-0.5 text-[10.5px]',
                  status === 'notified' && pending
                    ? 'bg-gat-greenDarkest text-white hover:bg-gat-green'
                    : 'border border-border bg-white text-text-body hover:border-gat-green hover:text-gat-greenDarkest',
                ].join(' ')}
              >
                {d.busy === status ? 'Saving…' : text}
              </button>
            ))}
          </div>
        ) : null}
        {d.error ? (
          <div role="alert" className="mt-1 text-[11px] text-[#B71C1C]">
            {d.error}
          </div>
        ) : null}
      </li>
    );
  };

  const waiting = tab === 'whatsnew' && list.canApprove ? list.items.filter((r) => r.status === 'pending') : [];
  const hiddenOnes = tab === 'whatsnew' && list.canApprove ? list.items.filter((r) => r.status === 'hidden') : [];
  const published =
    tab === 'whatsnew' && list.canApprove ? list.items.filter((r) => r.status !== 'pending' && r.status !== 'hidden') : list.items;
  const badge = badgeText(counts.forYou);
  const label = `Notifications${counts.forYou ? `, ${counts.forYou > 99 ? '99+' : counts.forYou} unread` : ''}${counts.whatsNew ? ", new in What's new" : ''}`;

  const empty =
    tab === 'foryou'
      ? chip === 'all'
        ? "You're all caught up. Tasks assigned to you, tags and @mentions, views shared with you, PO's and wins on your records, and overdue tasks show up here."
        : 'Nothing of this kind yet.'
      : 'Nothing new yet. Changes to GlobalOps that apply to you will show up here.';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Notifications"
        className="relative flex items-center justify-center w-9 h-9 rounded-md border border-border bg-white hover:border-gat-green focus:outline-none focus:ring-2 focus:ring-gat-green/30"
      >
        <Icon name="bell" size={17} color={counts.forYou ? GREEN : '#555'} />
        {badge ? (
          <span
            key={badge}
            data-testid="bell-badge"
            className="go-pop-in absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C62828] text-white text-[10px] font-bold leading-[18px] text-center shadow"
          >
            {badge}
          </span>
        ) : counts.whatsNew ? (
          <span data-testid="bell-dot" className="go-pop-in absolute top-1 right-1 w-2 h-2 rounded-full bg-gat-green" />
        ) : null}
      </button>

      {open &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            aria-modal="false"
            aria-label="Notifications"
            className="go-drop-in fixed z-[1000] bg-white border border-border rounded-lg shadow-2xl text-text-dark flex flex-col"
            style={{
              top: coords.top,
              right: coords.right,
              width: 'min(420px, calc(100vw - 16px))',
              maxHeight: 'min(640px, calc(100vh - 88px))',
            }}
          >
            {settings ? (
              <div className="flex flex-col min-h-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <button
                    type="button"
                    onClick={() => setSettings(null)}
                    className="flex items-center gap-1 text-[11.5px] font-semibold text-text-body hover:text-text-dark"
                  >
                    <Icon name="chevronLeft" size={13} /> Back
                  </button>
                  <span className="text-[12.5px] font-bold">Notification settings</span>
                  <span className="w-10" />
                </div>
                <div className="px-4 py-3 overflow-y-auto">
                  {settings.loading ? (
                    <div className="py-8 text-center text-[12px] text-text-muted">Loading…</div>
                  ) : (
                    <>
                      {settings.error && (
                        <div role="alert" className="mb-2 text-[11.5px] text-[#B71C1C]">
                          {settings.error}
                        </div>
                      )}
                      {settings.kinds && (
                        <>
                          <div className="text-[10px] uppercase font-bold tracking-[1.4px] text-text-muted mb-1">In the bell</div>
                          {Object.entries(settings.kinds).map(([k, l]) => (
                            <Toggle key={k} label={l} on={settings.inApp && settings.inApp[k]} onChange={(v) => setPref('inApp', k, v)} />
                          ))}
                          <div className="text-[10px] uppercase font-bold tracking-[1.4px] text-text-muted mt-3 mb-1">Emails</div>
                          {Object.entries(settings.emailKinds || {}).map(([k, l]) => (
                            <Toggle key={k} label={l} on={settings.email && settings.email[k]} onChange={(v) => setPref('email', k, v)} />
                          ))}
                          <p className="mt-3 text-[10.5px] text-text-muted">
                            What's new always lists the changes to GlobalOps that apply to the pages you can open.
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between shrink-0 px-4 pt-3">
                  <div role="tablist" aria-label="Notification lists" className="flex gap-4">
                    {TABS.map(([key, text, countKey]) => [key, text, counts[countKey]]).map(([key, text, c]) => (
                      <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={tab === key}
                        onClick={() => {
                          setTab(key);
                          setNotes({});
                        }}
                        className={[
                          'pb-2 text-[12.5px] font-bold border-b-2 flex items-center gap-1.5',
                          tab === key
                            ? 'border-gat-green text-gat-greenDarkest'
                            : 'border-transparent text-text-muted hover:text-text-dark',
                        ].join(' ')}
                      >
                        {text}
                        {c ? (
                          <span className="px-1.5 rounded-full bg-gat-green/10 text-gat-greenDarkest text-[10px]">{badgeText(c)}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 pb-2">
                    <button
                      type="button"
                      onClick={markAll}
                      disabled={!counts.forYou && !counts.whatsNew}
                      className="px-2 py-1 rounded text-[11px] font-semibold text-gat-greenDarkest hover:bg-gat-green/10 disabled:opacity-40 disabled:cursor-default"
                    >
                      Mark all read
                    </button>
                    <button
                      type="button"
                      onClick={showSettings}
                      aria-label="Notification settings"
                      title="Notification settings"
                      className="p-1.5 rounded hover:bg-surface-2"
                    >
                      <Icon name="settings" size={14} color="#666" />
                    </button>
                  </div>
                </div>
                {/* the chips wrap and never shrink: in the flex column a long list squeezed a
                  scrolling row down to its scrollbar, hiding the chips (10/11/26) */}
                {tab === 'foryou' ? (
                  <div className="flex flex-wrap shrink-0 gap-1.5 px-4 py-2 border-y border-border">
                    {FOR_YOU_CHIPS.map((c) => (
                      <Chip key={c.key} on={chip === c.key} onClick={() => setChip(c.key)}>
                        {c.label}
                      </Chip>
                    ))}
                  </div>
                ) : (
                  <div className="shrink-0 border-b border-border" />
                )}
                <div className="overflow-y-auto min-h-0 flex-1" aria-live="polite">
                  {list.error && (
                    <div role="alert" className="mx-4 my-3 px-3 py-2 rounded bg-[#FFEBEE] text-[#B71C1C] text-[11.5px] font-semibold">
                      {list.error}
                    </div>
                  )}
                  {list.loading && !list.items.length ? (
                    <div className="py-10 text-center text-[12px] text-text-muted">Loading…</div>
                  ) : !list.items.length && list.loaded && !list.error ? (
                    <div className="go-fade-in px-8 py-10 text-center">
                      <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-gat-green/10 flex items-center justify-center">
                        <Icon name={tab === 'foryou' ? 'check' : 'bell'} size={18} color={GREEN} />
                      </div>
                      <div className="text-[12px] text-text-body leading-relaxed">{empty}</div>
                    </div>
                  ) : tab === 'whatsnew' ? (
                    <>
                      {waiting.length > 0 && (
                        <section aria-label="Waiting for your approval" className="border-b-2 border-[#F2C94C]">
                          <div className="px-4 pt-2.5 pb-1.5 bg-[#FFF6DA]">
                            <div className="flex items-center gap-1.5 text-[10.5px] uppercase font-bold tracking-[1.2px] text-[#8A6100]">
                              <Icon name="alert" size={12} color="#8A6100" /> Waiting for your approval ({waiting.length})
                            </div>
                            <p className="m-0 mt-0.5 text-[10.5px] text-[#6B5200] leading-snug">
                              Nobody else sees these yet. Notify everyone puts them in people's bells; Publish quietly lists them without a
                              notification.
                            </p>
                          </div>
                          <ul className="m-0 p-0 list-none">{waiting.map(releaseCard)}</ul>
                        </section>
                      )}
                      {published.length > 0 && <ul className="m-0 p-0 list-none">{published.map(releaseCard)}</ul>}
                      {hiddenOnes.length > 0 && (
                        <details className="border-t border-border">
                          <summary className="px-4 py-2 text-[11px] font-bold text-text-muted cursor-pointer select-none">
                            Hidden from others ({hiddenOnes.length})
                          </summary>
                          <ul className="m-0 p-0 list-none">{hiddenOnes.map(releaseCard)}</ul>
                        </details>
                      )}
                    </>
                  ) : (
                    <ul className="m-0 p-0 list-none">
                      {list.items.map((item) => {
                        const key = `f${item.id}`;
                        const meta = KIND_META[item.kind] || KIND_META.assigned;
                        const unread = !item.read;
                        return (
                          <li key={key} className="go-fade-in border-b border-border last:border-b-0">
                            <button
                              type="button"
                              onClick={() => openForYou(item)}
                              className={[
                                'w-full text-left flex gap-3 px-4 py-2.5 hover:bg-[#F6F9F3]',
                                unread ? 'bg-[#F3F8F1]' : 'bg-white',
                              ].join(' ')}
                            >
                              <span
                                className="shrink-0 mt-0.5 w-7 h-7 rounded-full flex items-center justify-center"
                                style={{ background: meta.bg }}
                              >
                                <Icon name={meta.icon} size={13} color={meta.color} />
                              </span>
                              <span className="flex-1 min-w-0">
                                <span
                                  className={[
                                    'block text-[12px] leading-snug',
                                    unread ? 'font-semibold text-text-dark' : 'text-text-body',
                                  ].join(' ')}
                                >
                                  {item.title}
                                </span>
                                {item.body ? <span className="block text-[11px] text-text-muted truncate mt-0.5">{item.body}</span> : null}
                                <span className="block text-[10.5px] text-text-muted mt-0.5">
                                  {[item.actor, timeAgo(item.createdAt)].filter(Boolean).join(' · ')}
                                  {meta.label ? (
                                    <span className="ml-1.5 font-semibold" style={{ color: meta.color }}>
                                      {meta.label}
                                    </span>
                                  ) : null}
                                </span>
                                {notes[key] ? (
                                  <span role="status" className="block text-[11px] text-[#B71C1C] mt-1">
                                    {notes[key]}
                                  </span>
                                ) : null}
                              </span>
                              {unread ? (
                                <span aria-label="unread" className="shrink-0 mt-2 w-2 h-2 rounded-full bg-gat-green" />
                              ) : (
                                <span className="shrink-0 w-2" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {list.next && list.items.length > 0 && (
                    <div className="py-3 text-center">
                      <button
                        type="button"
                        onClick={() => load(tab, { more: true })}
                        disabled={list.loading}
                        className="px-4 py-1.5 rounded-md border border-gat-green text-[11.5px] font-bold text-gat-greenDarkest hover:bg-gat-green/10 disabled:opacity-50"
                      >
                        {list.loading ? 'Loading…' : 'Load more'}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>,
          document.body
        )}
    </>
  );
}
